import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText,
  Plus,
  LogOut,
  ClipboardList,
  Archive,
  Shield,
  UserCog,
  History,
  BarChart3,
  Menu,
  X,
  ShieldCheck,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const getInitials = (display: string | undefined): string => {
  if (!display?.trim()) return 'U';
  const parts = display.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] ?? '';
    const b = parts[1][0] ?? '';
    return (a + b).toUpperCase();
  }
  return display.trim().slice(0, 2).toUpperCase();
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = () => {
    setMobileNavOpen(false);
    logout();
    navigate('/login');
  };

  const handleToggleMobileNav = () => {
    setMobileNavOpen((open) => !open);
  };

  const handleCloseMobileNav = useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  const handleNavLinkClick = () => {
    setMobileNavOpen(false);
  };

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const baseNavigation: NavItem[] = [
    { name: 'Plantillas', href: '/templates', icon: FileText },
    { name: 'Crear plantilla', href: '/templates/create', icon: Plus },
    { name: 'Llenar consentimientos', href: '/consents', icon: ClipboardList },
    { name: 'Consentimientos guardados', href: '/consent-forms', icon: Archive },
    { name: 'Autorización de datos', href: '/autorizacion-datos', icon: ShieldCheck },
    { name: 'Historial por paciente', href: '/historial-paciente', icon: History },
    { name: 'Estadísticas', href: '/estadisticas', icon: BarChart3 },
    ...(user?.role === 'admin'
      ? [
          { name: 'Auditoría', href: '/auditoria', icon: Shield },
          { name: 'Administrar permisos', href: '/permisos', icon: UserCog },
        ]
      : []),
  ];

  const displayName = user?.name || user?.username || 'Usuario';
  const roleLabel = user?.role === 'admin' ? 'Administrador' : 'Usuario';

  const navLinkClass = (href: string) => {
    const active = isActive(href);
    return [
      'flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
      active
        ? 'bg-slate-900 text-white shadow-md shadow-slate-900/15'
        : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm border border-transparent hover:border-slate-200/80',
    ].join(' ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/60">
      <header className="sticky top-0 z-40 border-b border-slate-200/90 bg-white/85 backdrop-blur-md shadow-sm shadow-slate-200/40">
        <div
          className="h-1 w-full bg-gradient-to-r from-slate-800 via-blue-700 to-teal-600"
          aria-hidden
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-[4.25rem] items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center">
              <Link
                to="/templates"
                className="group flex min-w-0 items-center gap-3 rounded-xl py-1 pr-2 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                aria-label="Ir a plantillas"
              >
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-800 to-blue-800 p-1.5 shadow-md ring-1 ring-white/20">
                  <img
                    src="/logo.png"
                    alt=""
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <div className="min-w-0 text-left">
                  <h1 className="truncate text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                    Consentimientos informados
                  </h1>
                  <p className="truncate text-xs font-medium text-slate-500 sm:text-sm">
                    Hospital Divino Salvador de Sopó
                  </p>
                </div>
              </Link>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
              <div className="hidden items-center gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/80 py-1.5 pl-1.5 pr-4 sm:flex">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-sm font-bold text-white shadow-inner"
                  aria-hidden
                >
                  {getInitials(displayName)}
                </div>
                <div className="min-w-0 max-w-[10rem] lg:max-w-[14rem]">
                  <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
                  <p className="truncate text-xs text-slate-500">{roleLabel}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="hidden items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:flex"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                <span className="hidden lg:inline">Cerrar sesión</span>
              </button>

              <button
                type="button"
                onClick={handleToggleMobileNav}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 md:hidden"
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-nav-panel"
                aria-label={mobileNavOpen ? 'Cerrar menú' : 'Abrir menú de navegación'}
              >
                {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <nav
          className="hidden border-t border-slate-100 bg-white/60 backdrop-blur-sm md:block"
          aria-label="Navegación principal"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-2 py-3">
              {baseNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.href} to={item.href} className={navLinkClass(item.href)}>
                    <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      </header>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Menú">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            aria-label="Cerrar menú"
            onClick={handleCloseMobileNav}
          />
          <div
            id="mobile-nav-panel"
            className="absolute right-0 top-0 flex h-full w-[min(100%,20rem)] flex-col border-l border-slate-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4">
              <span className="text-sm font-semibold text-slate-800">Menú</span>
              <button
                type="button"
                onClick={handleCloseMobileNav}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-4">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-sm font-bold text-white"
                aria-hidden
              >
                {getInitials(displayName)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900">{displayName}</p>
                <p className="truncate text-xs text-slate-500">{roleLabel}</p>
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto p-3" aria-label="Enlaces">
              <div className="flex flex-col gap-1">
                {baseNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={handleNavLinkClick}
                      className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                        active
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </nav>
            <div className="border-t border-slate-100 p-3">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>

      <footer className="mt-auto border-t border-slate-200/80 bg-white/50 py-4 text-center text-xs text-slate-500">
        Sistema de consentimientos informados · Uso institucional
      </footer>
    </div>
  );
};

export default Layout;
